import React, { FC } from 'react';
import { View } from 'react-native';
import { scale } from '@components/frame';
import ColorsWrapper from '@styles/colors';
import {
  isPhone,
  isWeb,
  withERP,
} from '../../../../../../../../../helpers';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import LinkIcon from '@v2/context/ProductionBatch/linkIcon';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import { SmallLoading } from '@components/loading';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import { TourTarget } from '@components/tours';
import { BATCH_TOUR_IDS } from '@components/tours/config/machines/batch/constants';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import StatusBadge from '@components/common/StatusBadge';
import ReadyPcsQuantity from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';
import { ProductionBatchStatistics } from '@interfaces/search';
import { WithColor } from '@styles/colors';

import { ItemName, LinkText, Period, ProductivityStatus, LineWithTimelineAndTime } from './components';
import { useManageBatchDetailsBlockContent } from './hooks/useManageBatchDetailsBlockContent';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';


// Re-export sub-components for backward compatibility
export { ItemName, LinkText, Period, ProductivityStatus } from './components';

export interface BatchDetailsBlockContentProps extends WithColor {
  batch: ProductionBatchStatistics | null;
  withImages?: boolean;
  maxDetailsWidth: number;
  bg?: string;
  batchLoading?: boolean;
}

const BatchDetailsBlockContent: FC<BatchDetailsBlockContentProps> = ({
  C,
  batch,
  withImages,
  maxDetailsWidth,
  bg = 'Background/Light/Field',
}) => {
  const {
    isMachinesScreen,
    isWithERP,
    selectedItem,
    offset,
    batchLoaded,
    events,
    setEvents,
    isBatchPerspective,
    borderStyles,
    menuItemTextStyles,
    borderRadiusConfig,
    onProductivityStatusBlockClick,
    onBatchLinkClick,
    onBatchNumberClick,
    onBatchImageClick,
    batchLinkColumnName,
    offsetInSeconds,
    shadowStyles,
  } = useManageBatchDetailsBlockContent({ batch, C, withImages, maxDetailsWidth, bg });

  return (
    <>
      <TourTarget tourId={BATCH_TOUR_IDS.DETAILS_BLOCK}>
        <View
          testID={TEST_IDS.runningBatch.block}
          style={[{ maxWidth: maxDetailsWidth }, borderRadiusConfig, { backgroundColor: C(bg) }, isWeb && shadowStyles]}
        >
          {withImages && (
            <ImagesContainer
              {...{
                maxDetailsWidth,
                panelViewMode: false,
                mode: ImagesContainerMode.ROW,
                documentRetrieveKey: DOCUMENT_ENTITY_TYPE.Batch,
                documentRetrieveId: batch?.productionBatchId,
                onImagePress: onBatchImageClick,
              }}
            />
          )}

          <TourTarget tourId={WELCOME_TOUR_IDS.BATCH_INFO}>
            <TourTarget tourId={BATCH_TOUR_IDS.BATCH_NUMBER}>
              <InputLine
            {...{
              borderBottomRadius: false,
              borderTopRadius: !withImages,
              data: [],
              onlyLabel: false,
              index: 0,
              i: {
                name: 'Batch',
                isText: isBatchPerspective,
                ...(isBatchPerspective
                  ? { value: batch?.batchNumber }
                  : {
                      ValueComponent: <ItemName {...{ C, onPress: onBatchNumberClick, text: batch?.batchNumber }} />,
                    }),
              },
              C,
              loading: !batchLoaded,
              bg,
            }}
          />
            </TourTarget>
          </TourTarget>

          <TourTarget tourId={WELCOME_TOUR_IDS.BATCH_LINK}>
            <TourTarget tourId={BATCH_TOUR_IDS.BATCH_LINK}>
              <InputLine
                {...{
                  borderBottomRadius: false,
                  borderTopRadius: false,
                  data: [],
                  onlyLabel: false,
                  index: 0,
                  i: {
                    name: batchLinkColumnName,
                ValueComponent: (
                  <View style={[{ flexDirection: isPhone ? 'row-reverse' : 'row', gap: scale(9), alignItems: 'center', justifyContent: 'flex-end' }]}>
                    <LinkIcon
                      isWithERP={withERP(isWithERP)}
                      linkStatus={batch?.linkStatus}
                      ncFileStatus={batch?.ncFileStatus}
                      width={scale(24)}
                      height={scale(24)}
                    />
                    <LinkText {...{ C, isWithERP, links: batch?.links, linkStatus: batch?.linkStatus, ncFileStatus: batch?.ncFileStatus }} />
                  </View>
                ),
                action: onBatchLinkClick,
                  },
                  C,
                  loading: !batchLoaded,
                  bg,
                }}
              />
            </TourTarget>
          </TourTarget>

          {!isMachinesScreen && (
          <InputLine
            {...{
              borderBottomRadius: false,
              borderTopRadius: false,
              data: [],
              onlyLabel: false,
              index: 0,
              i: {
                name: 'Machine',
                value: batch?.machineShortName,
                isText: true,
              },
              C,
              loading: !batchLoaded,
              bg,
            }}
          />
        )}

        {!isMachinesScreen && (
          <InputLine
            {...{
              borderBottomRadius: false,
              borderTopRadius: false,
              data: [],
              onlyLabel: false,
              index: 0,
              i: {
                name: batch?.isInProgress ? 'Started' : 'Period',
                ValueComponent: <Period C={C} startedOn={batch?.startedOn} endedOn={!batch?.isInProgress ? batch?.endedOn : undefined} />,
              },
              C,
              loading: !batchLoaded,
              bg,
            }}
          />
        )}

          <TourTarget tourId={WELCOME_TOUR_IDS.BATCH_READY}>
            <TourTarget tourId={BATCH_TOUR_IDS.READY_QUANTITY}>
              <InputLine
                {...{
                  borderBottomRadius: true,
                  borderTopRadius: false,
                  data: [{}],
                  onlyLabel: false,
                  index: 0,
                  i: {
                    name: 'Ready',
                    ValueComponent: <ReadyPcsQuantity C={C} batch={batch} />,
                  },
                  C,
                  loading: !batchLoaded,
                  bg,
                }}
              />
            </TourTarget>
          </TourTarget>
        </View>
      </TourTarget>

      <View style={[{ maxWidth: maxDetailsWidth }, isWeb && shadowStyles, borderRadiusConfig, { backgroundColor: C(bg), marginTop: scale(12) }]}>
        {batch?.isInProgress && (
          <TourTarget tourId={WELCOME_TOUR_IDS.BATCH_PRODUCTIVITY}>
            <TourTarget tourId={BATCH_TOUR_IDS.PRODUCTIVITY_STATUS_BLOCK}>
              <ProductivityStatus
                {...{
                  testID: TEST_IDS.runningBatch.productivityStatusBlock,
                  batch,
                  C,
                  machineStatus: selectedItem.machineStatus,
                  onPress: onProductivityStatusBlockClick,
                  offset,
                  menuItemTextStyles,
                  bg,
                  borderStyles,
                  events,
                  borderRadiusConfig: {
                    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
                  },
                }}
              />
            </TourTarget>
          </TourTarget>
        )}

        <TourTarget tourId={WELCOME_TOUR_IDS.BATCH_STATUS_ROW}>
          <TourTarget tourId={BATCH_TOUR_IDS.BATCH_STATUS_BLOCK}>
            <LineWithTimelineAndTime
              {...{
                bg,
                productionBatchId: batch?.productionBatchId,
                borderStyles: {},
                menuItemTextStyles,
                borderRadiusConfig: !batch?.isInProgress
                  ? { borderRadius: scale(SCREEN_BORDER_RADIUS) }
                  : {
                      borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                      borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                    },
                name: 'Batch Status',
                C,
                StatusElement: batch ? (
                  <StatusBadge C={C} isClosed={batch?.isClosed} isInProgress={batch?.isInProgress} separateTextAndIconForMobile={isPhone} />
                ) : (
                  <SmallLoading />
                ),
                timeValue: batch?.actualTotalTimeTotalInSeconds != null ? batch?.actualTotalTimeTotalInSeconds + offsetInSeconds : 0,
                targetTimeValue: batch?.scheduledTargetTotalTimeTotalInSeconds,
                shouldRunTime: batch?.actualTotalTimeTotalInSeconds != null && batch?.isInProgress,
                withoutTimeline: batch?.isClosed,
                startedOn: batch?.startedOn,
                setEvents,
              }}
            />
          </TourTarget>
        </TourTarget>
      </View>
    </>
  );
};

export default ColorsWrapper(BatchDetailsBlockContent);
